import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LeadDetailPanel } from "./lead-detail-panel";
import { crmApi } from "./api";
import { LEAD_STATUSES, leadStatus } from "./types";
import type { Lead, LeadCampaign } from "./types";

const isEarlyStatus = (s: string) => {
  const low = s.trim().toLowerCase();
  return low === "fresh lead" || low === "new";
};

/** Full record page — the reference-style view: breadcrumb + status header,
    activity tabs on the left, details rail on the right. */
export function LeadRecordPage({ customers = false }: { customers?: boolean }) {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const cache = useQueryClient();
  const [campaigns, setCampaigns] = useState<LeadCampaign[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [customStatuses, setCustomStatuses] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const overdueQuery = useQuery({ queryKey: ["crm-sla-overdue"], queryFn: crmApi.slaOverdue, refetchInterval: 60000 });
  const overdueMap = useMemo(
    () => new Map((overdueQuery.data ?? []).map(row => [row.id, row.reasons])),
    [overdueQuery.data],
  );

  const reload = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const [nextCampaigns, nextLeads] = await Promise.all([crmApi.campaigns(), crmApi.leads()]);
      setCampaigns(nextCampaigns);
      setLeads(nextLeads);
      void cache.invalidateQueries({ queryKey: ["crm-sla-overdue"] });
    } catch (e) {
      if (!silent) setError(e instanceof Error ? e.message : "Unable to load CRM data.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [cache]);

  useEffect(() => {
    void reload();
    const interval = window.setInterval(() => void reload(true), 20_000);
    const onFocus = () => void reload(true);
    const onVisible = () => { if (document.visibilityState === "visible") onFocus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [reload]);

  const lead = leads.find(l => l.id === leadId) ?? null;
  // Source of truth is the data: fresh=true lives in Leads, fresh=false in
  // Customers. Entry points (notifications, follow-ups, calendar, notes)
  // always link to /crm/leads/:id, so derive the breadcrumb + back target
  // from the lead itself, not the route that opened it.
  const effectiveCustomers = lead ? !lead.fresh : customers;
  useEffect(() => {
    if (!lead || !leadId) return;
    if (customers !== effectiveCustomers) {
      navigate(`/crm/${effectiveCustomers ? "customers" : "leads"}/${leadId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id, effectiveCustomers]);
  const statuses = useMemo(
    () => Array.from(new Set([...LEAD_STATUSES, ...customStatuses, ...leads.map(leadStatus)])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customStatuses, leads.map(leadStatus).join("|")],
  );
  // Fresh/New live in Leads only — Customers hide that early stage.
  const visibleStatuses = useMemo(
    () => (lead && !lead.fresh ? statuses.filter(s => !isEarlyStatus(s)) : statuses),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lead?.fresh, statuses.join("|")],
  );

  const updateLead = async (id: string, data: Partial<Omit<Lead, "id" | "createdAt">>) => {
    try { await crmApi.updateLead(id, data); }
    catch (e) { setError(e instanceof Error ? e.message : "Unable to save lead."); return; }
    await reload(true);
  };

  const deleteLead = async (id: string) => {
    const wasFresh = leads.find(l => l.id === id)?.fresh ?? true;
    try { await crmApi.deleteLead(id); }
    catch (e) { setError(e instanceof Error ? e.message : "Unable to remove lead."); return; }
    navigate(wasFresh ? "/crm/leads" : "/crm/customers");
  };

  if (loading) {
    return <div className="p-8 text-center text-xs text-ink-3">Loading lead…</div>;
  }
  if (!lead) {
    return (
      <div className="flex flex-col items-center gap-3 p-12 text-center">
        <p className="text-sm font-semibold text-ink">Lead not found</p>
        <p className="max-w-sm text-xs text-ink-3">{error || "It may have been deleted."}</p>
        <button onClick={() => navigate("/crm/leads")} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white">
          Back to Leads
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {error && <p role="alert" className="px-6 pt-4 text-sm text-red-600">{error}</p>}
      <div className="min-h-0 flex-1">
        <LeadDetailPanel
          lead={lead}
          campaigns={campaigns}
          statuses={visibleStatuses}
          onAddStatus={status => setCustomStatuses(prev => [...prev, status])}
          onSave={updateLead}
          onDelete={(id) => void deleteLead(id)}
          onBack={() => navigate(effectiveCustomers ? "/crm/customers" : "/crm/leads")}
          listLabel={effectiveCustomers ? "Customers" : "Leads"}
          overdueReasons={overdueMap.get(lead.id)}
        />
      </div>
    </div>
  );
}
